import _ from 'lodash';
import { SEARCH_ITEM_LOCATIONS, SearchItemLocation } from '@/main/app.constants';
import { InitializeMode, PersistenceLevel, Store } from '@/core/flux.service';
import { ExploreModalStoreState, Folder, Item, SearchParams } from '@/explorer/exploreModal.types';
import { RootEnum } from 'sdk/api/FoldersApi';

export class ExploreFolderModalStore extends Store {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';
  static readonly storeName = 'sqExploreFolderModalStore';

  initialize(initializeMode?: InitializeMode) {
    const saveState = this.state && initializeMode !== 'FORCE';
    this.state = this.immutable({
      initialFolderId: undefined,
      tableFilter: saveState ? this.state.get('tableFilter') : SEARCH_ITEM_LOCATIONS.MY_FOLDER,
      itemTotalForTable: 0,
      pageSize: saveState ? this.state.get('pageSize') : 5,
      pageNumber: 1,
      searchParams: { types: ['Folder'] },
      items: [],
      loading: false,
      folder: undefined,
      sortAscending: false,
      sortProperty: saveState ? this.state.get('sortProperty') : 'name',
      showResults: false,
      workbookId: undefined,
      tableRoot: undefined,
    });
  }

  get initialFolderId(): string | undefined {
    return this.state.get('initialFolderId');
  }

  get workbookId(): string | undefined {
    return this.state.get('workbookId');
  }

  get tableFilter(): SearchItemLocation {
    return this.state.get('tableFilter');
  }

  get searchParams(): SearchParams {
    return this.state.get('searchParams');
  }

  get items(): Item[] {
    return this.state.get('items');
  }

  get loading(): boolean {
    return this.state.get('loading');
  }

  get folder(): Folder {
    return this.state.get('folder');
  }

  get sortAscending(): boolean {
    return this.state.get('sortAscending');
  }

  get sortProperty(): string {
    return this.state.get('sortProperty');
  }

  get showResults(): boolean {
    return this.state.get('showResults');
  }

  get tableRoot(): RootEnum | undefined {
    return this.state.get('tableRoot');
  }

  getItemTotalForTable(): number {
    return this.state.get('itemTotalForTable');
  }

  getPageSizeByTable(): number {
    return this.state.get('pageSize');
  }

  getPageNumberForTable(): number {
    return this.state.get('pageNumber');
  }

  dehydrate() {
    return _.omit(this.state.serialize(), [
      'effectivePermissions',
      'workbookId',
      'initialFolderId',
      'searchParams',
      'items',
      'folder',
      'tableFilter',
      'itemTotalForTable',
      'pageNumber',
      'sortAscending',
      'sortProperty',
      'loading',
      'showResults',
      'tableRoot',
    ]);
  }

  rehydrate(dehydratedState: ExploreModalStoreState) {
    this.state.merge(dehydratedState);
  }

  protected readonly handlers = {
    /**
     * Clears the state for the currently loaded seeq content image.
     */
    EXPLORE_FOLDER_MODAL_CLEAR: () => {
      this.initialize();
    },

    EXPLORE_FOLDER_MODAL_SET_INITIAL_FOLDER_ID: (payload: { initialFolderId?: string }) => {
      this.state.set('initialFolderId', payload.initialFolderId);
    },

    /**
     * Sets the workbook ID
     *
     * @param payload - Object container
     * @param payload.workbookId - a workbook ID
     */
    EXPLORE_FOLDER_MODAL_SET_WORKBOOK_ID: (payload: { workbookId?: string }) => {
      this.state.set('workbookId', payload.workbookId);
    },

    EXPLORE_FOLDER_MODAL_SET_TABLE_FILTER: (payload: { tableFilter: SearchItemLocation }) => {
      this.state.set('tableFilter', payload.tableFilter);
    },

    EXPLORE_FOLDER_MODAL_SET_ITEM_TOTAL_FOR_TABLE: (payload: { itemTotalForTable: number }) => {
      this.state.set('itemTotalForTable', payload.itemTotalForTable);
    },

    EXPLORE_FOLDER_MODAL_SET_PAGE_SIZE: (payload: { pageSize: number }) => {
      this.state.set('pageSize', payload.pageSize);
    },

    EXPLORE_FOLDER_MODAL_SET_PAGE_NUMBER: (payload: { pageNumber: number }) => {
      this.state.set('pageNumber', payload.pageNumber);
    },

    EXPLORE_FOLDER_MODAL_SET_ITEMS: (payload: { items: { id: string; name: string }[] }) => {
      this.state.set('items', payload.items);
    },

    EXPLORE_FOLDER_MODAL_SET_SEARCH_PARAMS: ({ field, value }: { field: string; value: string }) => {
      const searchParams = this.state.get('searchParams');

      if (value === '') {
        this.state.set('searchParams', _.omit(searchParams, field));
      } else {
        this.state.set('searchParams', _.assign({}, searchParams, { [field]: value }));
      }
    },

    EXPLORE_FOLDER_MODAL_SET_LOADING: (payload: { loading: boolean }) => {
      this.state.set('loading', payload.loading);
    },

    EXPLORE_FOLDER_MODAL_SET_FOLDER: (payload: { folder?: Folder }) => {
      this.state.set('folder', payload.folder);
    },

    EXPLORE_FOLDER_MODAL_SET_SORT: (payload: { sortProperty: string; sortAscending: boolean }) => {
      this.state.merge(_.pick(payload, ['sortProperty', 'sortAscending']));
    },

    EXPLORE_FOLDER_MODAL_SET_SHOW_RESULTS: (payload: { showResults: boolean }) => {
      this.state.set('showResults', payload.showResults);
    },

    EXPLORE_FOLDER_MODAL_SET_TABLE_ROOT: (payload: { root?: string }) => {
      this.state.set('tableRoot', payload.root);
    },
  };
}
